import { useId } from 'react';

import { cn } from '@/utils/classname';

/**
 * Props for the ToggleSwitch component.
 */
export interface ToggleSwitchProps {
  /**
   * Whether the toggle is in the "on" state.
   */
  checked: boolean;

  /**
   * Handler called when toggle state changes.
   * @param checked - The new checked state
   */
  onChange: (checked: boolean) => void;

  /**
   * Optional unique identifier for the toggle button.
   * If not provided, React 19's useId() generates a stable ID.
   */
  id?: string;

  /**
   * Optional disabled state. When true, the toggle cannot be interacted with.
   */
  disabled?: boolean;

  /**
   * Accessible label for screen readers.
   * Recommended for accessibility compliance.
   */
  ariaLabel?: string;

  /**
   * Additional CSS classes to apply to the toggle button.
   */
  className?: string;

  /**
   * Test ID for Playwright/testing.
   * Defaults to `toggle-${id}` if not provided.
   */
  'data-testid'?: string;
}

/**
 * ToggleSwitch - A standardized toggle switch component for boolean state.
 *
 * Visual Design:
 * - Rounded pill shape with sliding circle indicator
 * - Primary brand color when enabled, neutral when disabled
 * - Focus ring for keyboard navigation
 * - Disabled state with reduced opacity
 *
 * Accessibility:
 * - Proper ARIA role="switch" and aria-checked attributes
 * - Keyboard navigable with visible focus ring
 * - Disabled state prevents interaction
 *
 * @example
 * ```tsx
 * // Basic usage
 * <ToggleSwitch
 *   checked={isEnabled}
 *   onChange={setIsEnabled}
 *   ariaLabel="Enable feature"
 * />
 *
 * // With custom ID and disabled state
 * <ToggleSwitch
 *   id="feature-toggle"
 *   checked={isEnabled}
 *   onChange={setIsEnabled}
 *   disabled={isLoading}
 *   ariaLabel="Enable feature"
 * />
 * ```
 */
export function ToggleSwitch({
  checked,
  onChange,
  id,
  disabled,
  ariaLabel,
  className,
  'data-testid': testId,
}: ToggleSwitchProps) {
  // React 19 useId() provides stable ID generation when id prop is not provided
  const generatedId = useId();
  const toggleId = id ?? generatedId;

  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      id={toggleId}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        // Base styles: pill shape with fixed dimensions
        'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors duration-200 ease-in-out',
        // Focus styles: accessible focus ring
        'focus:ring-chariot-brand-primary focus:ring-2 focus:ring-offset-2 focus:outline-hidden',
        // Interaction states
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
        // Background color: brand primary when checked, neutral disabled when unchecked
        checked ? 'bg-chariot-brand-primary' : 'bg-chariot-text-disabled',
        className
      )}
      data-testid={testId ?? `toggle-${toggleId}`}
    >
      <span
        className={cn(
          // Thumb styles: small circle with shadow
          'inline-block h-3 w-3 transform rounded-full transition-transform duration-200 ease-in-out',
          'bg-white shadow-xs',
          // Position: slides between left and right
          checked ? 'translate-x-5' : 'translate-x-1'
        )}
      />
    </button>
  );
}
